/**
 * PilotTrialPlannerPage.tsx — Eight-week pilot trial schedule & log
 *
 * Captures everything needed to spin a pilot trial up the day a PO arrives:
 *   - Trial location, depth, isolation distance
 *   - 2-3 shortlisted diffusers
 *   - Instrumentation list (CTD, ADV, off-gas SOTE, pressure logger)
 *   - Weekly schedule with sample log
 *   - Methodology references (ASCE 2-06, BS EN 12255-15)
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { FlaskConical, ArrowLeft, Plus, Trash2, Save, Printer } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted } from '@/lib/sessionPersistence';
import { toast } from 'sonner';

const KEY = 'rb_pilot_trial_v1';

interface Diffuser {
  label: string;
  supplier: string;
  modelRef: string;
  modulesAtPilot: number;
}

interface Instrument {
  label: string;
  qty: number;
  source: string;
  acceptanceCriteria: string;
}

interface WeeklyEntry {
  week: number;
  startDate: string;
  activity: string;
  samples: string;
  data: string;
  notes: string;
}

interface PilotState {
  trialLocation: string;
  trialDepthM: number;
  isolationFromIntakeM: number;
  startDate: string;
  durationWeeks: number;
  methodStatementRef: string;
  riskAssessmentRef: string;
  diffusers: Diffuser[];
  instruments: Instrument[];
  weeks: WeeklyEntry[];
  successCriteria: string;
}

const DEFAULT_STATE: PilotState = {
  trialLocation: 'Cordoned bay near west embankment, away from inlet & draw-off',
  trialDepthM: 3.0,
  isolationFromIntakeM: 80,
  startDate: '',
  durationWeeks: 8,
  methodStatementRef: '',
  riskAssessmentRef: '',
  diffusers: [
    { label: 'Candidate A', supplier: '', modelRef: '', modulesAtPilot: 1 },
    { label: 'Candidate B', supplier: '', modelRef: '', modulesAtPilot: 1 },
  ],
  instruments: [
    { label: 'Multi-parameter CTD sonde (T, DO, pH, ORP, turbidity)', qty: 2, source: 'YSI EXO2 / In-Situ Aqua TROLL', acceptanceCriteria: 'Calibrated; ±0.1 mg/L DO' },
    { label: 'Off-gas hood for in-situ SOTE (ASCE 2-06)', qty: 1, source: 'Custom / Sanitaire kit', acceptanceCriteria: '±5% on SOTE per ASCE 2-06' },
    { label: 'Pressure logger per diffuser feed', qty: 3, source: 'Onset HOBO U20 / equivalent', acceptanceCriteria: '±0.1 kPa, 1-min logging' },
    { label: 'Acoustic Doppler velocimeter (ADV)', qty: 1, source: 'Nortek Vectrino / Sontek 16-MHz', acceptanceCriteria: 'Plume velocity ±0.01 m/s' },
    { label: 'Iron / calcium / NOM grab samplers', qty: 8, source: 'Standard 1 L glass amber', acceptanceCriteria: 'Weekly schedule, UKAS lab' },
    { label: 'Membrane visual inspection camera', qty: 1, source: 'Underwater ROV camera', acceptanceCriteria: 'Photo record per inspection' },
  ],
  weeks: [
    { week: 0, startDate: '', activity: 'Mobilisation, mooring install, instrument calibration', samples: 'Baseline grabs ×3', data: 'CTD pre-trial profile', notes: '' },
    { week: 1, startDate: '', activity: 'Diffuser commissioning, low-flow startup', samples: 'Daily grabs', data: 'Continuous CTD, pressure', notes: '' },
    { week: 2, startDate: '', activity: 'Clean-water SOTE measurement (ASCE 2-06)', samples: 'Off-gas every 30 min', data: 'SOTE per candidate', notes: '' },
    { week: 3, startDate: '', activity: 'Steady-state real-water α-factor tracking', samples: 'Twice-weekly grabs', data: 'Continuous CTD, ADV plume mapping', notes: '' },
    { week: 4, startDate: '', activity: 'Fouling-rate tracking, pressure drift logged', samples: 'Iron / calcium grabs', data: 'Pressure rise per diffuser', notes: '' },
    { week: 5, startDate: '', activity: 'Mid-trial visual inspection of membranes', samples: 'NOM samples', data: 'Photographic record', notes: '' },
    { week: 6, startDate: '', activity: 'Plume mapping under varied wind conditions', samples: '', data: 'ADV transects ×3', notes: '' },
    { week: 7, startDate: '', activity: 'Recovery cleaning trial (if needed)', samples: 'Pre/post grabs', data: 'SOTE recovery measurement', notes: '' },
    { week: 8, startDate: '', activity: 'Demobilisation, final inspection, data archive', samples: 'Final grabs', data: 'CTD profile, asset photos', notes: '' },
  ],
  successCriteria:
    'Each candidate: (1) measured in-situ SOTE within 20% of model prediction; ' +
    '(2) α-factor stable across the 8-week run; ' +
    '(3) pressure drift below 0.3 kPa/week; ' +
    '(4) plume mixing radius within 25% of predicted; ' +
    '(5) no taste & odour excursion measured at the intake. ' +
    'Final ranking weighted by SOTE, fouling, plume coverage and pilot-engineer judgement.',
};

export default function PilotTrialPlannerPage() {
  const navigate = useNavigate();
  const [s, setS] = useState<PilotState>(() => loadPersisted<PilotState>(KEY, DEFAULT_STATE));

  useEffect(() => { savePersisted(KEY, s); }, [s]);

  const updateD = (i: number, patch: Partial<Diffuser>) =>
    setS({ ...s, diffusers: s.diffusers.map((d, j) => j === i ? { ...d, ...patch } : d) });
  const addD = () => setS({ ...s, diffusers: [...s.diffusers, { label: `Candidate ${String.fromCharCode(65 + s.diffusers.length)}`, supplier: '', modelRef: '', modulesAtPilot: 1 }] });
  const removeD = (i: number) => setS({ ...s, diffusers: s.diffusers.filter((_, j) => j !== i) });

  const updateI = (i: number, patch: Partial<Instrument>) =>
    setS({ ...s, instruments: s.instruments.map((x, j) => j === i ? { ...x, ...patch } : x) });
  const addI = () => setS({ ...s, instruments: [...s.instruments, { label: '', qty: 1, source: '', acceptanceCriteria: '' }] });
  const removeI = (i: number) => setS({ ...s, instruments: s.instruments.filter((_, j) => j !== i) });

  const updateW = (i: number, patch: Partial<WeeklyEntry>) =>
    setS({ ...s, weeks: s.weeks.map((w, j) => j === i ? { ...w, ...patch } : w) });

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={FlaskConical}
        title="Pilot Trial Planner"
        subtitle="Eight-week side-by-side trial schedule, instrumentation, success criteria and weekly log"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={() => { savePersisted(KEY, s); toast.success('Saved'); }}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={() => window.print()}
              className="inline-flex items-center gap-2 rounded-lg bg-rb-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-rb-brand/90">
              <Printer className="w-4 h-4" /> Print
            </button>
          </div>
        }
      />

      {/* Header block */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-3">Trial header</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="md:col-span-2">
            <label className="text-xs text-slate-400 block mb-1">Trial location</label>
            <input value={s.trialLocation} onChange={e => setS({ ...s, trialLocation: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Depth at trial location (m)</label>
            <input type="number" step={0.1} value={s.trialDepthM}
              onChange={e => setS({ ...s, trialDepthM: Number(e.target.value) })}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Isolation from intake (m)</label>
            <input type="number" value={s.isolationFromIntakeM}
              onChange={e => setS({ ...s, isolationFromIntakeM: Number(e.target.value) })}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Planned start date</label>
            <input type="date" value={s.startDate}
              onChange={e => setS({ ...s, startDate: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Duration (weeks)</label>
            <input type="number" value={s.durationWeeks}
              onChange={e => setS({ ...s, durationWeeks: Number(e.target.value) })}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Method statement ref</label>
            <input value={s.methodStatementRef}
              onChange={e => setS({ ...s, methodStatementRef: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Risk assessment ref</label>
            <input value={s.riskAssessmentRef}
              onChange={e => setS({ ...s, riskAssessmentRef: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
        </div>
      </div>

      {/* Diffusers */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-100">Shortlisted diffusers</div>
          <button onClick={addD} className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-slate-100">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
        <div className="space-y-2">
          {s.diffusers.map((d, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-center">
              <input value={d.label} onChange={e => updateD(i, { label: e.target.value })}
                placeholder="Label"
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100" />
              <input value={d.supplier} onChange={e => updateD(i, { supplier: e.target.value })}
                placeholder="Supplier"
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100" />
              <input value={d.modelRef} onChange={e => updateD(i, { modelRef: e.target.value })}
                placeholder="Model ref"
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100" />
              <input type="number" value={d.modulesAtPilot}
                onChange={e => updateD(i, { modulesAtPilot: Number(e.target.value) })}
                placeholder="Modules"
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100" />
              <button onClick={() => removeD(i)} className="text-slate-500 hover:text-red-400 justify-self-end">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Instruments */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-100">Instrumentation list</div>
          <button onClick={addI} className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-slate-100">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
        <div className="space-y-2">
          {s.instruments.map((it, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
              <input value={it.label} onChange={e => updateI(i, { label: e.target.value })}
                placeholder="Instrument"
                className="md:col-span-4 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100" />
              <input type="number" value={it.qty} onChange={e => updateI(i, { qty: Number(e.target.value) })}
                className="md:col-span-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100" />
              <input value={it.source} onChange={e => updateI(i, { source: e.target.value })}
                placeholder="Source / make-model"
                className="md:col-span-3 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100" />
              <input value={it.acceptanceCriteria} onChange={e => updateI(i, { acceptanceCriteria: e.target.value })}
                placeholder="Acceptance criteria"
                className="md:col-span-3 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100" />
              <button onClick={() => removeI(i)} className="text-slate-500 hover:text-red-400 justify-self-end md:col-span-1">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Weekly schedule */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-3">Weekly schedule & log</div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-2 py-1.5 text-left text-slate-300">Wk</th>
                <th className="px-2 py-1.5 text-left text-slate-300">Start</th>
                <th className="px-2 py-1.5 text-left text-slate-300">Activity</th>
                <th className="px-2 py-1.5 text-left text-slate-300">Samples</th>
                <th className="px-2 py-1.5 text-left text-slate-300">Data captured</th>
                <th className="px-2 py-1.5 text-left text-slate-300">Notes</th>
              </tr>
            </thead>
            <tbody>
              {s.weeks.map((w, i) => (
                <tr key={i} className="border-b border-slate-800">
                  <td className="px-2 py-1 text-slate-100 font-semibold">{w.week}</td>
                  <td className="px-2 py-1">
                    <input type="date" value={w.startDate}
                      onChange={e => updateW(i, { startDate: e.target.value })}
                      className="bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100" />
                  </td>
                  <td className="px-2 py-1">
                    <input value={w.activity}
                      onChange={e => updateW(i, { activity: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100" />
                  </td>
                  <td className="px-2 py-1">
                    <input value={w.samples}
                      onChange={e => updateW(i, { samples: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100" />
                  </td>
                  <td className="px-2 py-1">
                    <input value={w.data}
                      onChange={e => updateW(i, { data: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100" />
                  </td>
                  <td className="px-2 py-1">
                    <input value={w.notes}
                      onChange={e => updateW(i, { notes: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Success criteria */}
      <div className="rounded-xl border border-emerald-700 bg-emerald-900/15 p-4">
        <div className="text-sm font-semibold text-emerald-200 mb-2">Success criteria & decision rule</div>
        <textarea value={s.successCriteria}
          onChange={e => setS({ ...s, successCriteria: e.target.value })}
          rows={5}
          className="w-full bg-slate-900 border border-emerald-700/50 rounded px-2 py-1 text-xs text-slate-100" />
      </div>
    </div>
  );
}
